import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@mybridge/ui/button';
import { City, Country, State } from 'country-state-city';
import {
  getUserProfileInfo,
  updateUserDetails,
} from 'packages/mybridgev4/store/actions/user-profile.actions';
import { Box, Flex, HStack, Text, Spinner } from '@mybridge/ui';

import { AddCircle, BackArrow, BackArrowRTL, EditIcon, LocationIcon } from '@mybridge/icons';
import styles from './settings.module.scss';
import { LocationTagsInput } from 'v4/components/location-tags-input';
import { isMobile } from 'react-device-detect';
import { IconButton } from '@mybridge/ui/icon-button';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useDirection } from '@mybridge/ui/custom-hooks/useDirection';

const Location = ({ setIsEditing,setSelectedAccountComponent,setEditMode,editMode }) => {
  const dispatch = useDispatch();
  const { t } = useTranslator();
  const direction = useDirection();
  const { userProfileInfo, updateStatus, isProfileUpdated } = useSelector(
    (state) => state.userProfile
  );
  const [errors, setErrors] = useState({});
  const [country, setCountry] = useState();
  const [state, setState] = useState();
  const [city, setCity] = useState();
  const [location, setLocation] = useState();
  const [loading, setLoading] = useState(false);
  const countries = Country.getAllCountries();

  const states = useMemo(() => {
    return State.getStatesOfCountry(country?.value);
  }, [country]);
  const cities = useMemo(
    () => City.getCitiesOfState(country?.value, state?.value),
    [country, state]
  );

  useEffect(() => {
    if (userProfileInfo?.id) {
      try {
        const country_ = Country.getAllCountries()?.find(
          (c) => c.name === userProfileInfo?.country
        );
        const state_ = State.getAllStates()?.find(
          (c) => c.name === userProfileInfo?.state
        );
        const city_ = City.getCitiesOfState(
          country_?.isoCode,
          state_?.isoCode
        )?.find((c) => c.name === userProfileInfo?.city);
        if (country_?.isoCode) {
          setCountry({
            label: country_?.name,
            value: country_?.isoCode,
          });
        }
        if (state_?.name) {
          setState({
            label: state_?.name,
            value: state_?.isoCode,
          });
        }
        if (city_?.name) {
          setCity({
            label: city_?.name,
            value: city_?.name,
          });
        }
      } catch (e) {
        console.error(e);
      }
    }
    // return () => setValues({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfileInfo]);

  useEffect(() => {
    if (isProfileUpdated) {
      setEditMode(false);
    }
  }, [isProfileUpdated]);

  const onClickSave = () => {
    const errorObject = {};
    // if (!country) {
    //   errorObject.country = 'Please select a country';
    // }

    if (Object.keys(errorObject).length > 0) {
      setErrors(errorObject);
      return;
    }
    const payload = {
      country: country,
      state: state,
      city: city,
      location: [city, state, country].filter((part) => part && part !== 'undefined').join(', '),
    };
    setLoading(true);
    dispatch(updateUserDetails(payload))
      .then(() => {
        dispatch(getUserProfileInfo());
        setLoading(false);
        onClickCancel();
      })
      .catch(() => setLoading(false));
  };

  const onClickCancel = () => {
    setEditMode(false);
    setErrors({});
    setIsEditing('');
    setSelectedAccountComponent('account');
  };

  if (editMode) {
    return (
      <Box w={['100%', '350px']} py="15px" p={['10px', '15px 0']}>
        {isMobile ? (
          <>
            <Text color="brandGray.700" fontSize={['20px', '16px']}>
              {t('profileDetails.location') || "Location"}
            </Text>
            <Text fontSize={['md', 'sm']} color="brandGray.700" mt="10px">
              {t('settingAccount.addAddress') || "Add your address here"}
            </Text>
          </>
        ) : (
          <HStack>
            <Button
              variant="transparent"
              leftIcon={direction === 'rtl' ? <BackArrowRTL color="#868585" /> : <BackArrow color="#868585" />}
              color="#868585"
              onClick={onClickCancel}
              p={0}
            />
            <Text color="brandGray.700">{t('profileDetails.location') || "Location"}</Text>
          </HStack>
        )}
        <>
          <Box gap={3} my={3}>
            <LocationTagsInput
              className={isMobile ? styles.dataDisplayBoxes : undefined}
              variant={isMobile ? undefined : 'greyRounded'}
              keyName="location"
              onChange={(e) => {
                setCity(e[0]?.city);
                setState(e[0]?.state);
                setCountry(e[0]?.country);
              }}
            />
          </Box>
          <Flex
            justifyContent={isMobile ? 'flex-end' : 'flex-start'}
            columnGap="2"
            mt={['5%', '2%']}
          >
            <Button
              onClick={onClickCancel}
              borderRadius={['30px', '30px']}
              bg="#E4E6EB"
            >
              {t('buttons.cancel') || "Cancel"}
            </Button>
            <Button
              disabled={updateStatus === 'pending'}
              onClick={onClickSave}
              color="#fff"
              bg={['#3D5A80', '#3D5A80']}
              borderRadius={['30px', '30px']}
            >
              {updateStatus === 'pending' ? <Spinner /> : t('buttons.save') || 'Save'}
            </Button>
          </Flex>
        </>
      </Box>
    );
  }

  return (
    <Box p={['8px', '15px 0']}>
      <Flex justifyContent="space-between">
        {/* <Box w={['80%', '350px']}> */}
        <Box flex={1} maxW="350px" mr={4} minW="0">
          {isMobile && !editMode && (
            <>
              <Box mt={3}>
                {userProfileInfo?.city && (
                  <Box mt={3} display="flex" alignItems="center" minW="0">
                    <LocationIcon
                      width="24"
                      height="24"
                      style={{ marginRight: '10px' }}
                      color="#868585"
                    />{' '}
                    <Text
                      color="#868585"
                      width="95%" // Set the width to 90%
                      whiteSpace="nowrap" // Prevent text from wrapping to the next line
                      overflow="hidden" // Hide overflow text
                      textOverflow="ellipsis"
                      // maxW={'300px'}
                    >
                      {/* {userProfileInfo?.city
                        ? userProfileInfo?.city + ', '
                        : ''}
                      {userProfileInfo?.state
                        ? userProfileInfo?.state + ', '
                        : ''}
                      {userProfileInfo?.country ? userProfileInfo?.country : ''} */}
                      {userProfileInfo?.location}
                    </Text>
                  </Box>
                )}
                <Text color="#868585" fontSize={['12px']} ml="35px">
                  {t('profileDetails.location') || "Location"}
                </Text>
              </Box>
            </>
          )}
          {!editMode && !isMobile && (
            <Box flex="1">
              {/* <Text color="brandGray.700">{t('profileDetails.location') || "Location"}</Text> */}
              <Text color="brandGray.700" fontSize={['16px', '14px', '14px', '16px']}>{t('profileDetails.location') || "Location"}</Text>
            </Box>
          )}
          {!isMobile && (
            <>
              {userProfileInfo?.city && (
                <Box className={styles.dataDisplayBoxes} mt={3}>
                  {userProfileInfo?.location
                    ? (userProfileInfo.location[0] === ","
                        ? userProfileInfo.location.slice(1).trim()
                        : userProfileInfo.location)
                    : `${userProfileInfo?.city ? userProfileInfo.city + ', ' : ''}${
                        userProfileInfo?.state ? userProfileInfo.state + ', ' : ''
                      }${userProfileInfo?.country ? userProfileInfo.country : ''}`}
                </Box>
              )}
            </>
          )}
        </Box>
        {isMobile && !editMode ? (
          <IconButton
            bg="none"
            onClick={() => {
              setEditMode(true);
              setIsEditing('location');
              setSelectedAccountComponent('location');
            }}
            icon={<EditIcon />}
          />
        ) : (
          !editMode && (
            <Button
              variant="ghost"
              borderRadius={"30px"}
              color="black"
              onClick={() => {
                setEditMode(true);
                setIsEditing('location');
                setSelectedAccountComponent('location');
              }}
              leftIcon={<AddCircle width={20} height={20} />}
              fontSize={['14px', '13px', '13px', '14px']}
            >
              {t('settingAccount.editLocation') || "Edit Location"}
            </Button>
          )
        )}
      </Flex>
    </Box>
  );
};

export default Location;
